import { stringifyEntityRef } from '@backstage/catalog-model';
import type { WaadooIconName } from '../ui/icons/waadooIcons';

/**
 * The sub-pages of « Créer » (`page:scaffolder`) and what the page does with
 * each: which ones anyone may open, which ones are tabs, and their French
 * labels. Plain functions, so the rules are tested without rendering.
 */

/** A sub-page attached to `page:scaffolder`, as its input gives it. */
export interface CreateSubPage {
  /** Relative to the page: `templates`, `tasks`, `actions`, `edit`… */
  path: string;
  /** The sub-page's own title (upstream, in English). */
  title?: string;
  element: JSX.Element;
}

/** The template list and its forms (`sub-page:scaffolder/templates`). */
export const TEMPLATES_PATH = 'templates';

/** The task pages (`sub-page:scaffolder/tasks`); its list is « Tâches ». */
export const TASKS_SUB_PAGE_PATH = 'tasks';

/**
 * The sub-pages a non-admin may open: the templates and their forms, and the
 * page of a task. The others (actions, template editor, templating
 * extensions) are technical: an admin's tabs. UX, not security — the
 * scaffolder's API and the permission policy decide what anyone can do.
 */
const MEMBER_PATHS: ReadonlySet<string> = new Set([
  TEMPLATES_PATH,
  TASKS_SUB_PAGE_PATH,
]);

export function isMemberSubPage(path: string): boolean {
  return MEMBER_PATHS.has(path);
}

const TAB_LABELS: Readonly<Record<string, string>> = {
  [TEMPLATES_PATH]: 'Modèles',
  actions: 'Actions',
  edit: 'Éditeur de modèles',
  'templating-extensions': 'Extensions de modèles',
};

const TAB_ICONS: Readonly<Record<string, WaadooIconName>> = {
  [TEMPLATES_PATH]: 'templateList',
  actions: 'scaffolderActions',
  edit: 'templateEditor',
  'templating-extensions': 'templatingExtensions',
};

export interface CreateTab {
  id: string;
  label: string;
  path: string;
  /** Before the label; none for a sub-page the app does not know. */
  icon?: WaadooIconName;
  /**
   * A technical tab, which only an admin may open (`isMemberSubPage`): it
   * carries the « admin » tag, after the separator.
   */
  adminOnly: boolean;
}

/**
 * An admin's tabs: every sub-page but the tasks, in the page's order, under
 * its French label — a sub-page the app does not know keeps its own title.
 * The tasks are reached from « Tâches », the tab at the end of the row that
 * opens the app's task list (createPage/CreatePage.tsx).
 */
export function adminTabs(pages: CreateSubPage[]): CreateTab[] {
  return pages
    .filter(page => page.path !== TASKS_SUB_PAGE_PATH)
    .map(page => ({
      id: page.path,
      label: TAB_LABELS[page.path] ?? page.title ?? page.path,
      path: page.path,
      icon: TAB_ICONS[page.path],
      adminOnly: !isMemberSubPage(page.path),
    }));
}

/**
 * Whether the path under the page (`templates/default/odoo-project`,
 * `tasks/<id>`) is one template's form or one task's page, where the page's
 * description and action do not apply.
 */
export function isDetailPath(rest: string): boolean {
  const [first, ...others] = rest.split('/').filter(Boolean);
  return (
    (first === TEMPLATES_PATH || first === TASKS_SUB_PAGE_PATH) &&
    others.length > 0
  );
}

/**
 * Whether the path under the page is one task's page (`tasks/<id>`), where
 * an admin's « Tâches » tab is the selected one.
 */
export function isTaskPath(rest: string): boolean {
  const [first, ...others] = rest.split('/').filter(Boolean);
  return first === TASKS_SUB_PAGE_PATH && others.length > 0;
}

/**
 * Si le chemin sous « templates/ » est le formulaire du modèle de chemin
 * `templatePath` — un modèle dont un module apporte son propre formulaire
 * (`createWizard`, components/appModules), tel que la sous-page des modèles
 * voit son chemin (`<namespace>/<nom>` : `default/odoo-project` pour
 * `template:default/odoo-project`).
 */
export function isWizardTemplatePath(
  rest: string,
  templatePath: string,
): boolean {
  return rest.replace(/\/+$/, '') === templatePath;
}

/**
 * The template path under « templates/ » of the page's path (what follows
 * `/create/`): `default/wagtail-project` for
 * `templates/default/wagtail-project`; undefined elsewhere — the list, a
 * task, a technical tab.
 */
export function templatePathOf(rest: string): string | undefined {
  const prefix = `${TEMPLATES_PATH}/`;
  return rest.startsWith(prefix) ? rest.slice(prefix.length) : undefined;
}

/**
 * The template ref of a template path under « templates/ »
 * (`default/wagtail-project` → `template:default/wagtail-project`), as the
 * scaffolder's route `:namespace/:templateName` reads it; undefined for
 * anything else (the list, a deeper path).
 */
export function templateRefOfPath(path: string): string | undefined {
  const parts = path.split('/').filter(Boolean);
  if (parts.length !== 2) return undefined;
  const [namespace, name] = parts;
  return stringifyEntityRef({ kind: 'Template', namespace, name });
}
